import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  CircularProgress,
  Button,
} from '@mui/material';

// Icons for different event types
import PunchClockIcon from '@mui/icons-material/PunchClock';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import SupportIcon from '@mui/icons-material/Support';
import ArchiveIcon from '@mui/icons-material/Archive';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import RateReviewIcon from '@mui/icons-material/RateReview';
import UndoIcon from '@mui/icons-material/Undo';
import SendIcon from '@mui/icons-material/Send';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useShiftHistoryStore } from '../../stores/root_store';
import type { ShiftHistoryItem } from '../../types';

interface EventDisplay {
  icon: React.ReactNode;
  action: string;
}

function getEventDisplay(item: ShiftHistoryItem): EventDisplay {
  const type = item.associated_type;

  // Handle null/undefined associated_type
  if (!type) {
    return { icon: <HelpOutlineIcon color="action" />, action: 'Unknown Action' };
  }

  // Clock events
  if (type.startsWith('clock:')) {
    if (type === 'clock:delete') {
      return { icon: <DeleteIcon color="action" />, action: 'Deleted Clock' };
    }
    return {
      icon: <PunchClockIcon color="action" />,
      action: type.endsWith('start') ? 'Clocked In' : 'Clocked Out',
    };
  }

  // Mentor events
  if (type.startsWith('mentor:')) {
    if (type === 'mentor:claim') return { icon: <RateReviewIcon color="action" />, action: 'Claimed Review' };
    if (type === 'mentor:approve') return { icon: <RateReviewIcon color="success" />, action: 'Approved Response' };
    if (type === 'mentor:deny') return { icon: <RateReviewIcon color="error" />, action: 'Denied Response' };
    if (type === 'mentor:unclaim') return { icon: <UndoIcon color="action" />, action: 'Unclaimed Review' };
    return { icon: <RateReviewIcon color="action" />, action: type.split(':')[1] };
  }

  // Response events
  if (type.startsWith('response:')) {
    if (type === 'response:recall') return { icon: <UndoIcon color="action" />, action: 'Recalled Response' };
    return { icon: <UndoIcon color="action" />, action: type.split(':')[1] };
  }

  // submit_response (no colon prefix)
  if (type === 'submit_response') {
    return { icon: <SendIcon color="action" />, action: 'Submitted Response' };
  }

  // Manager events
  if (type.startsWith('manager:')) {
    if (type === 'manager:close-quietly') return { icon: <DoNotDisturbIcon color="action" />, action: 'Closed Quietly' };
    if (type === 'manager:resolve') return { icon: <CheckCircleOutlineIcon color="action" />, action: 'Resolved Request' };
    if (type === 'manager:respond') return { icon: <QuestionAnswerIcon color="action" />, action: 'Manager Response' };
    if (type === 'manager:user-update') return { icon: <ManageAccountsIcon color="action" />, action: 'Updated User' };
    return { icon: <ManageAccountsIcon color="action" />, action: type.split(':')[1] };
  }

  // User events
  if (type.startsWith('user:')) {
    if (type === 'user:admin-settings-update') return { icon: <AdminPanelSettingsIcon color="action" />, action: 'Updated Admin Settings' };
    return { icon: <AdminPanelSettingsIcon color="action" />, action: type.split(':')[1] };
  }

  // Shift events
  if (type.startsWith('shift:')) {
    let action = 'Started Shift';
    if (type === 'shift:end') action = 'Ended Shift';
    if (type === 'shift:ephemeral-start') action = 'Started Ephemeral';
    if (type === 'shift:ephemeral-end') action = 'Ended Ephemeral';
    return { icon: <WorkHistoryIcon color="action" />, action };
  }

  // Request events
  if (type.startsWith('request:')) {
    if (type === 'request:claim') {
      return { icon: <AssignmentTurnedInIcon color="action" />, action: 'Claimed Request' };
    }
    if (type === 'request:release') {
      return { icon: <UndoIcon color="action" />, action: 'Released Request' };
    }
    if (type === 'request:close-other') {
      return { icon: <AssignmentIndIcon color="warning" />, action: "Closed Another's Task" };
    }
    return { icon: <AssignmentTurnedInIcon color="action" />, action: 'Request Action' };
  }

  // Collab invite events
  if (type.startsWith('collab_invite:')) {
    const verb = type.split(':')[1];
    let action = verb;
    if (verb === 'accept') action = 'Joined Collab';
    if (verb === 'invite-mutual') action = 'Invited Mutual';
    return { icon: <GroupAddIcon color="action" />, action };
  }

  // Assistant actions (help requests, etc.)
  if (type.startsWith('assistant:')) {
    if (type === 'assistant:help-request') {
      return { icon: <SupportIcon color="warning" />, action: 'Needs Help' };
    }
    return { icon: <SupportIcon color="action" />, action: type.split(':')[1] };
  }

  // Support actions
  if (type.startsWith('support:')) {
    const verb = type.split(':')[1];
    let action = verb;
    let icon: React.ReactNode = <ContactMailIcon color="action" />;

    if (verb === 'email-search') action = 'User Email Search';
    if (verb === 'sub-modify') action = 'Update Subscription';
    if (verb === 'sub-cancel') action = 'Cancel Subscription';
    if (verb === 'shift-edit') action = 'Edited Shift';
    if (verb === 'request-assign') {
      action = 'Assigned Request';
      icon = <AssignmentTurnedInIcon color="action" />;
    }
    if (verb === 'request-update-price') {
      action = 'Updated Credits/Price';
      icon = <LocalAtmIcon color="action" />;
    }
    if (verb === 'request/help/respond') {
      action = 'Support Response';
      icon = <SupportIcon color="action" />;
    }
    if (verb === 'archive') {
      action = 'Archived Request';
      icon = <ArchiveIcon color="action" />;
    }
    if (verb === 'request-unassign') {
      action = 'Unassigned Request';
      icon = <PersonRemoveIcon color="action" />;
    }
    if (verb === 'request-invite') {
      action = 'Invited to Request';
      icon = <PersonAddIcon color="action" />;
    }
    if (verb === 'refund') {
      action = 'Issued Refund';
      icon = <CurrencyExchangeIcon color="action" />;
    }
    return { icon, action };
  }

  // Message events
  if (type.startsWith('message:')) {
    let action = 'Sent message';
    if (type === 'message:qa') action = 'Sent QA message';
    return { icon: <ChatBubbleOutlineIcon color="action" />, action };
  }

  // Calendar events
  if (type.startsWith('calendar:')) {
    let action = 'Calendar Action';
    if (type === 'calendar:event-create') action = 'Created Event';
    if (type === 'calendar:event-delete') action = 'Deleted Event';
    if (type === 'calendar:event-update') action = 'Updated Event';
    return { icon: <CalendarMonthIcon color="action" />, action };
  }

  // Email events
  if (type.startsWith('email:')) {
    return { icon: <ForwardToInboxIcon color="action" />, action: 'Sent Email' };
  }

  // Dedicated events
  if (type.startsWith('dedicated:')) {
    if (type === 'dedicated:create-collab') return { icon: <GroupIcon color="action" />, action: 'Created Dedicated Collab' };
    if (type === 'dedicated:create-project') return { icon: <CreateNewFolderIcon color="action" />, action: 'Created Dedicated Project' };
    return { icon: <GroupIcon color="action" />, action: 'Dedicated Action' };
  }

  // Payroll events
  if (type.startsWith('payroll:')) {
    return {
      icon: <PaymentsIcon color="action" />,
      action: type.endsWith('run') ? 'Ran Payroll' : 'Payroll Action',
    };
  }

  // Default fallback
  return { icon: <HelpOutlineIcon color="action" />, action: type };
}

const TeamActivityItem = ({ item }: { item: ShiftHistoryItem }) => {
  const { icon, action } = getEventDisplay(item);
  const timeStr = new Date(item.date_created).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <ListItem divider sx={{ py: 1 }}>
      {icon && <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>}
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={500}>
              {action}
            </Typography>
            {item.user_name && (
              <Chip
                label={item.user_name}
                size="small"
                variant="outlined"
                clickable={!!item.user_uuid}
                component={item.user_uuid ? Link : 'div'}
                {...(item.user_uuid ? { to: `/manager/user/${item.user_uuid}` } : {})}
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            )}
          </Stack>
        }
        secondary={timeStr}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
      {item.request && (
        <Button
          component={Link}
          to={`/assistant/request/${item.request}`}
          size="small"
          sx={{ ml: 1, whiteSpace: 'nowrap' }}
        >
          View
        </Button>
      )}
    </ListItem>
  );
};

export const TeamActivityPanel = observer(() => {
  const shiftHistoryStore = useShiftHistoryStore();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      shiftHistoryStore.fetchBySupport(true);
    }
  }, [initialized, shiftHistoryStore]);

  const handleLoadMore = () => {
    shiftHistoryStore.fetchBySupport();
  };

  const handleRefresh = () => {
    shiftHistoryStore.fetchBySupport(true);
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Team Activity</Typography>
          <Button size="small" onClick={handleRefresh} disabled={shiftHistoryStore.loading}>
            Refresh
          </Button>
        </Stack>

        {shiftHistoryStore.loading && shiftHistoryStore.items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : shiftHistoryStore.items.length === 0 ? (
          <Typography color="text.secondary">No recent team activity</Typography>
        ) : (
          <>
            <List dense disablePadding>
              {shiftHistoryStore.items.map((item) => (
                <TeamActivityItem key={item.uuid} item={item} />
              ))}
            </List>
            {shiftHistoryStore.hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button onClick={handleLoadMore} disabled={shiftHistoryStore.loading}>
                  {shiftHistoryStore.loading ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}

        {shiftHistoryStore.error && (
          <Typography color="error" sx={{ mt: 1 }}>
            {shiftHistoryStore.error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});
